<template>
    <section>
        <titlebar :title-stack="['Orçamentos', model.id ? `${model.numero}` : 'Novo']">            
        </titlebar>
        <div class="section prd-page">
            <b-loading :active="loading" :is-full-page="false" />
            <div v-if="model.id" class="prd-context-bar">
                <div>
                    <span class="eyebrow">CLIENTE</span><strong>{{ model.clienteNome }}</strong
                    >
                    <div v-if="model.clienteContato" class="prd-client-contact">
                        <button
                            type="button"
                            class="prd-whatsapp-button"
                            title="Iniciar conversa no WhatsApp"
                            @click="abrirWhatsapp(model)"
                        >
                            <b-icon icon="whatsapp" type="is-success" size="is-small" />
                        </button>
                        <small>{{ model.clienteContato }}</small>
                    </div>
                    <small v-else>Contato não informado</small>
                </div>
                <div>
                    <span class="eyebrow">ATUALIZADO</span><strong>{{ dataHora(model.dataHoraCadastro) }}</strong
                    ><small>Versão v{{ model.versao }}</small>
                    <div class="prd-context-status"><prd-status-tag :status="model.status" tipo="orcamento" /></div>
                </div>
                <div v-if="podeVerValores">
                    <span class="eyebrow">TOTAL ORÇAMENTO</span><strong class="prd-total">{{ moeda(valorTotalItens) }}</strong>
                </div>
            </div>

            <div class="prd-layout">
                <main class="prd-main">
                    <div class="prd-tabs">
                        <button v-for="tab in tabsVisiveis" :key="tab.id" :class="{ active: aba === tab.id }" @click="aba = tab.id">
                            <b-icon :icon="tab.icon" size="is-small" />{{ tab.label }}<span v-if="tab.id === 'itens'">{{ model.itens.length }}</span>
                        </button>
                    </div>

                    <div v-show="aba === 'geral'" class="prd-card">
                        <div class="prd-card-head">
                            <div>
                                <h2>Dados do orçamento</h2>
                                <p>Identificação, prazo e condições comerciais.</p>
                            </div>
                        </div>
                        <div class="prd-card-body">
                            <div class="columns is-multiline">
                                <div class="column is-8">
                                    <b-field
                                        label="Cliente"
                                        :type="!model.clienteId && tentouSalvar ? 'is-danger' : null"
                                        :message="!model.clienteId && tentouSalvar ? 'Selecione o cliente.' : null"
                                    >
                                        <div class="prd-client-field">
                                            <b-autocomplete
                                                v-model="clienteBusca"
                                                class="prd-client-search"
                                                :data="clientes"
                                                field="nome"
                                                icon="account-search-outline"
                                                :loading="buscandoClientes"
                                                :disabled="!editavel"
                                                clearable
                                                open-on-focus
                                                @typing="buscarClientes"
                                                @select="selecionarCliente"
                                            >
                                                <template slot-scope="p"
                                                    ><strong>{{ p.option.nome }}</strong
                                                    ><small class="prd-option-detail">{{ p.option.documento || p.option.contato }}</small></template
                                                >
                                                <template #empty>Nenhum cliente encontrado</template>
                                            </b-autocomplete>
                                            <b-button
                                                v-if="isInRole('prd-clientes-alterar')"
                                                type="is-primary is-light"
                                                icon-left="account-plus-outline"
                                                tag="router-link"
                                                :to="{ name: 'cliente' }"
                                            >
                                                Novo cliente
                                            </b-button>
                                        </div>
                                    </b-field>
                                </div>
                                <div class="column is-4">
                                    <b-field label="Responsável"
                                        ><b-select v-model="model.responsavelId" expanded :disabled="!editavel"
                                            ><option v-for="r in responsaveis" :key="r.id" :value="r.id">{{ r.nome }}</option></b-select
                                        ></b-field
                                    >
                                </div>
                                <div class="column is-4">
                                    <b-field label="Condição de pagamento"
                                        ><b-select v-model="model.condicaoPagamentoId" expanded :disabled="!editavel"
                                            ><option :value="null">selecione...</option>
                                            <option v-for="c in condicoes" :key="c.id" :value="c.id">{{ c.nome }}</option></b-select
                                        ></b-field
                                    >
                                </div>
                                <div class="column is-4">
                                    <b-field label="Data de Pagamento"
                                        ><b-datepicker
                                            v-model="dataPagamento"
                                            expanded
                                            icon="calendar"
                                            :disabled="!editavel"
                                            trap-focus
                                            append-to-body
                                            position="is-top-left"
                                    /></b-field>
                                </div>
                                <div class="column is-4">
                                    <b-field label="Prazo de Entrega"
                                        ><b-datepicker
                                            v-model="dataEntrega"
                                            expanded
                                            icon="calendar"
                                            :disabled="!editavel"
                                            trap-focus
                                            append-to-body
                                            position="is-top-left"
                                    /></b-field>
                                </div>
                                <div class="column is-12">
                                    <b-switch v-model="model.emitirNotaFiscal" :disabled="!editavel || salvando">Emitir Nota Fiscal?</b-switch>
                                </div>
                                <div class="column is-12">
                                    <b-field label="Observações gerais"
                                        ><b-input
                                            v-model.trim="model.observacoes"
                                            type="textarea"
                                            rows="3"
                                            maxlength="4000"
                                            :disabled="!editavel"
                                    /></b-field>
                                    <div v-if="model.id && editavel && isInRole('prd-orcamentos-versionar')" class="prd-version-option">
                                        <div>
                                            <strong>Criar uma nova versão ao salvar</strong>
                                            <p>A versão atual será preservada no histórico e o orçamento avançará para v{{ model.versao + 1 }}.</p>
                                        </div>
                                        <b-switch v-model="mudarVersao" type="is-warning" :disabled="salvando">Mudar Versão?</b-switch>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-show="aba === 'itens'" class="prd-card">
                        <div class="prd-card-head">
                            <div>
                                <h2>Itens do orçamento</h2>
                                <p>Especificações, quantidades e valores do orçamento.</p>
                            </div>
                            <b-button v-if="editavelCadastro" type="is-primary is-light" icon-left="plus" @click="abrirItem()">Adicionar item</b-button>
                        </div>
                        <div class="prd-card-body prd-items-body">
                            <prd-empty-state
                                v-if="model.itens.length === 0"
                                icon="shape-outline"
                                title="Inclua o primeiro item"
                                description="Cada peça pode ter especificações e arquivos próprios."
                                ><b-button v-if="editavelCadastro" type="is-primary" icon-left="plus" class="mt-4" @click="abrirItem()"
                                    >Adicionar item</b-button
                                ></prd-empty-state
                            >
                            <article v-for="(item, index) in model.itens" :key="item.id || index" class="prd-item-card">
                                <div class="prd-item-index">{{ index + 1 }}</div>
                                <div class="prd-item-content">
                                    <div class="prd-item-title">
                                        <div>
                                            <strong>{{ item.descricao }}</strong>
                                            <span class="prd-item-summary">
                                                <span>{{ item.quantidade }} {{ item.unidadeMedida }}</span>
                                                <template v-if="podeVerValores">
                                                    <span class="prd-item-operator">×</span>
                                                    <strong class="prd-item-price">{{ moeda(item.valorUnitarioComercial || 0) }}</strong>
                                                </template>
                                                <span v-if="item.material" class="prd-item-material">{{ item.material }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <p v-if="item.dimensoes || item.processo || item.acabamento" class="prd-item-meta">
                                        {{ [item.dimensoes, item.processo, item.acabamento].filter(Boolean).join(' · ') }}
                                    </p>
                                    <div v-if="model.status >= 8" class="prd-analysis-summary">
                                        <div class="prd-analysis-heading">
                                            <span class="prd-analysis-title"><b-icon icon="clipboard-check-outline" size="is-small" /> Resultado da análise</span>
                                            <prd-status-tag :status="item.statusProducao" tipo="itemAnalise" />
                                        </div>
                                        <div v-if="item.prazoProducaoDias != null || item.observacoesProducao" class="prd-analysis-details">
                                            <div v-if="item.prazoProducaoDias != null" class="prd-analysis-detail">
                                                <b-icon icon="calendar-clock" size="is-small" />
                                                <div><small>Prazo de produção</small><strong>{{ item.prazoProducaoDias }} dias</strong></div>
                                            </div>
                                            <div v-if="item.observacoesProducao" class="prd-analysis-detail prd-analysis-notes">
                                                <b-icon icon="text-box-outline" size="is-small" />
                                                <div><small>Observações da análise</small><p>{{ item.observacoesProducao }}</p></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="item.valorUnitarioComercial != null" class="prd-commercial-value">
                                        <div></div>
                                        <div>
                                            <small>Subtotal</small><strong>{{ moeda((item.valorUnitarioComercial || 0) * item.quantidade) }}</strong>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="editavel" class="prd-item-actions">
                                    <b-button size="is-small" type="is-light" icon-left="pencil-outline" @click="abrirItem(item, index)" /><b-button
                                        v-if="editavelCadastro"
                                        size="is-small"
                                        type="is-danger is-light"
                                        icon-left="delete-outline"
                                        @click="removerItem(index)"
                                    />
                                </div>
                            </article>
                            <div v-if="model.itens.length" class="prd-items-total">
                                <span>Total do orçamento</span>
                                <strong>{{ moeda(valorTotalItens) }}</strong>
                            </div>
                        </div>
                    </div>

                    <div v-show="aba === 'arquivos'" class="prd-card">
                        <div class="prd-card-head">
                            <div>
                                <h2>Arquivos e desenhos</h2>
                                <p>Documentos vinculados ao orçamento ou a um item.</p>
                            </div>
                        </div>
                        <div class="prd-card-body">
                            <prd-anexos
                                ref="anexos"
                                :orcamento-id="model.id"
                                :unidade-id="unidadeId"
                                :anexos="model.anexos"
                                :itens="model.itens"
                                :somente-leitura="!editavel"
                                @adicionado="model.anexos.push($event)"
                                @removido="removerAnexoLocal"
                            />
                        </div>
                    </div>
                    <div v-if="model.id" v-show="aba === 'historico'" class="prd-card">
                        <div class="prd-card-head">
                            <div>
                                <h2>Linha do tempo</h2>
                                <p>Registro permanente das principais ações.</p>
                            </div>
                        </div>
                        <div class="prd-card-body">
                            <prd-historico entidade="orcamentos" :entidade-id="model.id" :unidade-id="unidadeId" />
                        </div>
                    </div>
                </main>

            </div>

            <footer class="prd-form-actions">
                <b-button type="is-light" native-type="button" icon-left="arrow-left" @click="$router.back()">
                    {{ $t('SISTEMA.CANCELAR') }}
                </b-button>
                <b-button
                    v-if="[1, 8].includes(model.status) && model.id && isInRole('prd-orcamentos-enviar-producao')"
                    type="is-info"
                    native-type="button"
                    icon-left="send-outline"
                    @click="acao('enviar-analise', 'O orçamento será enviado para análise da produção. Deseja continuar?')"
                >
                    Enviar para análise
                </b-button>
                <b-button v-if="model.status === 8 && isInRole('prd-orcamentos-gerar-pdf')" type="is-light" icon-left="printer" @click="imprimir">
                    Visualizar PDF
                </b-button>
                <b-button v-if="model.status === 8 && isInRole('prd-orcamentos-controlar-cliente')" type="is-info" icon-left="send-check-outline" @click="acao('marcar-enviado', 'Confirma que o orçamento foi enviado ao cliente?')">
                    Marcar enviado ao cliente
                </b-button>
                <b-button v-if="model.status === 2 && isInRole('prd-orcamentos-controlar-cliente')" type="is-success" icon-left="factory" @click="acao('aprovar-producao', 'Confirma a aprovação do cliente e o envio para fabricação?')">
                    Aprovado Produção
                </b-button>
                <b-button
                    v-if="editavel"
                    type="is-primary"
                    :loading="salvando"
                    native-type="button"
                    icon-left="content-save-outline"
                    @click="salvar"
                >
                    {{ $t('SISTEMA.SALVAR') }}
                </b-button>
            </footer>
        </div>

        <b-modal :active.sync="itemModal" has-modal-card trap-focus
            ><prd-orcamento-item-modal :item="itemSelecionado" :somente-valor="model.status === 8" @close="itemModal = false" @salvar="salvarItem"
        /></b-modal>
        <b-modal :active.sync="empresaModal" has-modal-card trap-focus
            ><div class="modal-card prd-company-modal">
                <header class="modal-card-head">
                    <div>
                        <p class="modal-card-title">Nova empresa</p>
                        <p class="is-size-7 has-text-grey">Vinculada ao cliente selecionado</p>
                    </div>
                    <button class="delete" @click="empresaModal = false" />
                </header>
                <section class="modal-card-body">
                    <b-field label="Razão social"><b-input v-model.trim="novaEmpresa.razaoSocial" /></b-field
                    ><b-field label="Nome fantasia"><b-input v-model.trim="novaEmpresa.nomeFantasia" /></b-field>
                    <div class="columns">
                        <div class="column">
                            <b-field label="CNPJ/CPF"><b-input v-model.trim="novaEmpresa.cpfCnpj" /></b-field>
                        </div>
                        <div class="column">
                            <b-field label="WhatsApp"><b-input v-model.trim="novaEmpresa.whatsapp" /></b-field>
                        </div>
                    </div>
                    <b-field label="E-mail"><b-input v-model.trim="novaEmpresa.email" type="email" /></b-field>
                </section>
                <footer class="modal-card-foot is-justify-content-flex-end">
                    <b-button @click="empresaModal = false">Cancelar</b-button><b-button type="is-primary" @click="salvarEmpresa">Salvar empresa</b-button>
                </footer>
            </div></b-modal
        >
    </section>
</template>

<script>
import http from '@/http'
import { mapGetters, mapState } from 'vuex'
import titlebar from '@/components/titlebar.vue'
import prdStatusTag from '@/components/prd/prdStatusTag.vue'
import prdEmptyState from '@/components/prd/prdEmptyState.vue'
import prdAnexos from '@/components/prd/prdAnexos.vue'
import prdHistorico from '@/components/prd/prdHistorico.vue'
import prdOrcamentoItemModal from '@/components/prd/prdOrcamentoItemModal.vue'
const novo = () => ({
    id: 0,
    versaoId: 0,
    numero: 0,
    versao: 1,
    versaoConcorrencia: 0,
    clienteId: null,
    clienteEmpresaId: null,
    condicaoPagamentoId: null,
    responsavelId: null,
    status: 1,
    dataEntrega: null,
    dataPagamento: null,
    emitirNotaFiscal: false,
    observacoes: '',
    valorTotalComercial: 0,
    itens: [],
    anexos: [],
})
export default {
    name: 'PrdOrcamento',
    components: { titlebar, prdStatusTag, prdEmptyState, prdAnexos, prdHistorico, prdOrcamentoItemModal },
    data() {
        return {
            loading: false,
            salvando: false,
            tentouSalvar: false,
            mudarVersao: false,
            aba: 'geral',
            model: novo(),
            dataEntrega: null,
            dataPagamento: null,
            clientes: [],
            clienteBusca: '',
            buscandoClientes: false,
            empresas: [],
            responsaveis: [],
            condicoes: [],
            itemModal: false,
            itemSelecionado: {},
            itemIndice: -1,
            empresaModal: false,
            novaEmpresa: { razaoSocial: '', nomeFantasia: '', cpfCnpj: '', whatsapp: '', email: '' },
            tabs: [
                { id: 'geral', label: 'Dados gerais', icon: 'file-document-outline' },
                { id: 'itens', label: 'Itens', icon: 'shape-outline' },
                { id: 'arquivos', label: 'Arquivos', icon: 'paperclip' },
                { id: 'historico', label: 'Histórico', icon: 'history' },
            ],
        }
    },
    computed: {
        ...mapState(['unidadeId', 'unidades', 'usuario']),
        ...mapGetters(['isInRole']),
        unidadeNome() {
            const unidade = this.unidades?.find((item) => item.id == this.unidadeId)
            return unidade?.nome
        },
        tabsVisiveis() {
            return this.tabs.filter((tab) => tab.id !== 'historico' || this.model.id)
        },
        editavel() {
            return this.isInRole('prd-orcamentos-alterar') 
        },
        editavelCadastro() {
            return this.editavel && (!this.model.id || this.model.status === 1)
        },
        podeVerValores() {
            return this.isInRole('prd-orcamentos-consultar')
        },
        valorTotalItens() {
            return this.model.itens
                .reduce((total, item) => total + Number(item.valorUnitarioComercial || 0) * Number(item.quantidade || 0), 0)
        },
    },
    watch: {
        unidadeId() {
            this.iniciar()
        },
    },
    created() {
        this.iniciar()
    },
    methods: {
        abrirWhatsapp(orcamento) {
            const celular = String(orcamento.clienteContato || '').replace(/\D/g, '')
            if (celular.length < 10) return

            const numeroWhatsapp = celular.length <= 11 ? `55${celular}` : celular
            const origem = this.unidadeNome ? `Aqui é da ${this.unidadeNome}. ` : ''
            const mensagem = `Olá, ${orcamento.clienteNome}! ${origem}Estamos entrando em contato com você. Tudo bem?`
            const url = `https://wa.me/${numeroWhatsapp}?text=${encodeURIComponent(mensagem)}`

            window.open(url, '_blank', 'noopener,noreferrer')
        },
        async iniciar() {
            if (!this.unidadeId) return
            this.loading = true
            try {
                await Promise.all([this.carregarApoio(), this.$route.params.id ? this.carregar() : Promise.resolve()])
                if (!this.model.responsavelId && this.usuario.funcionarioId) this.model.responsavelId = this.usuario.funcionarioId
            } catch (e) {
                this.erro(e)
            } finally {
                this.loading = false
            }
        },
        async carregarApoio() {
            const [r, c] = await Promise.all([
                http.get(`/api/prd/apoio/responsaveis?unidadeId=${this.unidadeId}`),
                http.get(`/api/prd/apoio/condicoes-pagamento?unidadeId=${this.unidadeId}`),
            ])
            this.responsaveis = r.data || []
            this.condicoes = c.data || []
        },
        async carregar() {
            const r = await http.get(`/api/prd/orcamentos/${this.$route.params.id}?unidadeId=${this.unidadeId}`)
            this.model = r.data
            this.mudarVersao = false
            this.dataEntrega = this.model.dataEntrega ? new Date(`${this.model.dataEntrega}`) : null
            this.dataPagamento = this.model.dataPagamento ? new Date(`${this.model.dataPagamento}`) : null
            this.clienteBusca = this.model.clienteNome
            await this.carregarEmpresas()
        },
        async buscarClientes(texto) {
            if (!texto || texto.length < 2) {
                this.clientes = []
                return
            }
            this.buscandoClientes = true
            try {
                const r = await http.get(`/api/prd/apoio/clientes?unidadeId=${this.unidadeId}&procurar=${encodeURIComponent(texto)}`)
                this.clientes = r.data || []
            } finally {
                this.buscandoClientes = false
            }
        },
        async selecionarCliente(c) {
            if (!c) {
                this.model.clienteId = null
                this.empresas = []
                return
            }
            this.model.clienteId = c.id
            this.model.clienteNome = c.nome
            this.model.clienteEmpresaId = null
            await this.carregarEmpresas()
        },
        async carregarEmpresas() {
            if (!this.model.clienteId) return
            const r = await http.get(`/api/prd/apoio/clientes/${this.model.clienteId}/empresas?unidadeId=${this.unidadeId}`)
            this.empresas = r.data || []
        },
        abrirItem(item = {}, index = -1) {
            this.itemSelecionado = item
            this.itemIndice = index
            this.itemModal = true
        },
        salvarItem(item) {
            if (this.itemIndice >= 0) this.$set(this.model.itens, this.itemIndice, item)
            else {
                item.sequencia = this.model.itens.length + 1
                this.model.itens.push(item)
            }
            this.itemModal = false
        },
        removerItem(i) {
            this.$buefy.dialog.confirm({
                message: 'Remover este item?',
                confirmText: 'Remover',
                cancelText: 'Cancelar',
                type: 'is-danger',
                onConfirm: () => this.model.itens.splice(i, 1),
            })
        },
        async salvar(voltarParaLista = true) {
            this.tentouSalvar = true
            if (!this.model.clienteId || !this.model.responsavelId || !this.model.itens.length) {
                this.aviso('Informe cliente, responsável e ao menos um item.')
                return false
            }
            const novoOrcamento = !this.model.id
            const itensAntesDeSalvar = novoOrcamento ? this.model.itens.slice() : []
            let salvo = false
            let anexosEnviados = true
            this.salvando = true
            try {
                this.model.dataEntrega = this.dataEntrega
                this.model.dataPagamento = this.dataPagamento
                const payload = Object.assign({}, this.model, { mudarVersao: this.mudarVersao })
                const r = await http.post(`/api/prd/orcamentos?unidadeId=${this.unidadeId}`, payload)
                this.model = r.data
                this.mudarVersao = false
                this.dataEntrega = this.model.dataEntrega ? new Date(this.model.dataEntrega) : null
                this.dataPagamento = this.model.dataPagamento ? new Date(this.model.dataPagamento) : null
                if (!novoOrcamento) {
                    this.sucesso('Orçamento salvo.')
                    if (voltarParaLista) await this.$router.push({ name: 'prd-orcamentos' })
                    return true
                }
                salvo = true
            } catch (e) {
                this.erro(e)
                if (!novoOrcamento) return false
            } finally {
                try {
                    if (novoOrcamento && salvo && this.model.id) {
                        anexosEnviados = await this.$refs.anexos.enviarPendentes(this.model.id, this.model.itens, itensAntesDeSalvar)
                    }
                } finally {
                    this.salvando = false
                }
            }
            if (!salvo) return false
            if (!anexosEnviados) {
                this.aviso('Orçamento salvo, mas alguns arquivos não foram anexados. Tente enviá-los novamente na aba Arquivos.')
                return false
            }
            this.sucesso('Orçamento salvo.')
            if (voltarParaLista) await this.$router.push({ name: 'prd-orcamentos' })
            return true
        },
        acao(endpoint, mensagem) {
            this.$buefy.dialog.confirm({
                title: 'Confirmar ação',
                message: mensagem,
                confirmText: 'Confirmar',
                cancelText: 'Cancelar',
                type: 'is-primary',
                hasIcon: true,
                onConfirm: async () => {
                    try {
                        await http.post(`/api/prd/orcamentos/${this.model.id}/${endpoint}?unidadeId=${this.unidadeId}`)
                        if (endpoint === 'enviar-analise' || endpoint === 'aprovar-producao') {
                            this.sucesso(endpoint === 'enviar-analise' ? 'Orçamento enviado para análise.' : 'Orçamento aprovado para produção.')
                            const podeAcessarDestino = endpoint === 'enviar-analise'
                                ? this.isInRole('prd-analise-consultar') || this.isInRole('prd-analise-alterar')
                                : this.isInRole('prd-pedidos-consultar') || this.isInRole('prd-pedidos-produzir') || this.isInRole('prd-pedidos-entregar')
                            await this.$router.push(podeAcessarDestino
                                ? { name: endpoint === 'enviar-analise' ? 'prd-analise-detalhe' : 'prd-producao-detalhe', params: { id: this.model.id } }
                                : { name: 'prd-orcamentos' })
                            return
                        }
                        this.sucesso('Status do orçamento atualizado.')
                        await this.carregar()
                    } catch (e) {
                        this.erro(e)
                    }
                },
            })
        },
        imprimir() {
            const numero = String(this.model.numero).padStart(4, '0')
            const nomeArquivo = `Orcamento-${numero}-V${this.model.versao}.pdf`
            window.open(`/api/prd/orcamentos/${this.model.id}/pdf/${nomeArquivo}?unidadeId=${encodeURIComponent(this.unidadeId)}`, '_blank', 'noopener,noreferrer')
        },
        async salvarEmpresa() {
            if (!this.novaEmpresa.razaoSocial) return this.aviso('Informe a razão social.')
            try {
                const r = await http.post(
                    `/api/prd/apoio/empresas?unidadeId=${this.unidadeId}`,
                    Object.assign({ id: 0, clienteId: this.model.clienteId, ativo: true }, this.novaEmpresa)
                )
                this.empresas.push(r.data)
                this.model.clienteEmpresaId = r.data.id
                this.empresaModal = false
                this.novaEmpresa = { razaoSocial: '', nomeFantasia: '', cpfCnpj: '', whatsapp: '', email: '' }
            } catch (e) {
                this.erro(e)
            }
        },
        removerAnexoLocal(a) {
            this.model.anexos = this.model.anexos.filter((x) => x.id !== a.id)
        },
        moeda(v) {
            return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },
        dataHora(v) {
            return v ? new Date(v).toLocaleString('pt-BR') : '—'
        },
        sucesso(m) {
            this.$buefy.toast.open({ message: m, type: 'is-success' })
        },
        aviso(m) {
            this.$buefy.toast.open({ message: m, type: 'is-warning' })
        },
        erro(e) {
            this.$buefy.toast.open({ message: e.data?.erro || 'Não foi possível concluir a operação.', type: 'is-danger' })
        },
    },
}
</script>

<style scoped>
.prd-page {
    background: var(--surface-page);
    min-height: calc(100vh - 110px);
    padding: 1rem 1rem !important;
}
.prd-context-bar {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 1rem;
    border: 1px solid rgba(22, 143, 138, 0.2);
    background: linear-gradient(125deg, var(--brand-teal-100), #f4f8fc);
    color: var(--text-strong);
    border-radius: 14px;
    padding: 1rem 1.25rem;
    margin-bottom: 1rem;
    box-shadow: 0 5px 16px rgba(31, 53, 78, 0.06);
}
.prd-context-bar > div {
    display: flex;
    flex-direction: column;
}
.prd-context-bar strong {
    color: var(--brand-navy-800);
}
.prd-context-bar small {
    color: var(--text-muted);
}
.prd-context-status {
    margin-top: 0.35rem;
}
.prd-client-contact {
    display: flex;
    align-items: center;
    gap: 0.3rem;
}
.prd-whatsapp-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
}
.prd-whatsapp-button:hover,
.prd-whatsapp-button:focus-visible {
    filter: brightness(0.85);
    outline: none;
}
.prd-context-bar .eyebrow {
    color: var(--brand-teal-600);
    font-weight: 800;
    opacity: 1;
}
.eyebrow {
    font-size: 0.65rem;
    letter-spacing: 0.12em;
    opacity: 0.7;
}
.prd-total {
    color: var(--brand-teal-600) !important;
    font-size: 1.25rem;
}
.prd-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
}
.prd-tabs {
    display: flex;
    gap: 0.25rem;
    background: white;
    border: 1px solid var(--border-soft);
    border-radius: 12px;
    padding: 0.35rem;
    margin-bottom: 0.85rem;
    overflow: auto;
}
.prd-tabs button {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    border: 0;
    background: transparent;
    padding: 0.65rem 0.85rem;
    border-radius: 8px;
    color: #65768a;
    white-space: nowrap;
    cursor: pointer;
}
.prd-tabs button.active {
    background: var(--brand-teal-100);
    color: var(--brand-navy-800);
    font-weight: 700;
}
.prd-tabs button span {
    background: #dfe9f5;
    border-radius: 999px;
    padding: 0.02rem 0.35rem;
    font-size: 0.7rem;
}
.prd-card {
    background: white;
    border: 1px solid var(--border-soft);
    border-radius: 14px;
    overflow: hidden;
}
.prd-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.2rem;
    border-bottom: 1px solid #edf0f4;
}
.prd-card-head h2 {
    font-weight: 700;
}
.prd-card-head p {
    font-size: 0.8rem;
    color: #8492a6;
}
.prd-card-body {
    padding: 1.2rem;
}
.prd-version-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    margin-bottom: 1.2rem;
    padding: 1rem 1.1rem;
    border: 1px solid #f0dca7;
    border-radius: 10px;
    background: #fffaf0;
}
.prd-version-option strong {
    color: var(--brand-navy-800);
}
.prd-version-option p {
    margin-top: 0.15rem;
    color: var(--text-muted);
    font-size: 0.78rem;
}
.prd-version-option .switch {
    flex: none;
}
.prd-field-action {
    gap: 0.4rem;
}
.prd-client-field {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
}
.prd-client-search {
    flex: 1;
    min-width: 0;
}
.prd-option-detail {
    display: block;
    color: #8492a6;
}
.prd-items-body {
    display: grid;
    gap: 0.75rem;
}
.prd-items-total {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 0.25rem;
    padding: 1rem 1.25rem;
    border-top: 2px solid var(--brand-teal-500);
    color: var(--brand-navy-800);
}
.prd-items-total span {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.prd-items-total strong {
    color: var(--brand-teal-600);
    font-size: 1.35rem;
}
.prd-item-card {
    display: flex;
    gap: 0.8rem;
    padding: 1rem;
    border: 1px solid #e3e9f0;
    border-radius: 12px;
}
.prd-item-index {
    flex: 0 0 32px;
    height: 32px;
    display: grid;
    place-items: center;
    background: var(--brand-teal-100);
    color: var(--brand-navy-700);
    border-radius: 9px;
    font-weight: 700;
}
.prd-item-content {
    flex: 1;
    min-width: 0;
}
.prd-item-title {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
}
.prd-item-title strong,
.prd-item-title span {
    display: block;
}
.prd-item-title span,
.prd-item-meta {
    font-size: 0.8rem;
    color: #738296;
}
.prd-item-summary {
    display: flex !important;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.3rem;
    margin-top: 0.15rem;
}
.prd-item-summary span,
.prd-item-summary strong {
    display: inline;
}
.prd-item-operator {
    color: var(--brand-teal-600) !important;
    font-weight: 700;
}
.prd-analysis-summary {
    margin-top: 0.85rem;
    border: 1px solid #dcebe9;
    border-radius: 10px;
    background: #f8fbfa;
    overflow: hidden;
}
.prd-analysis-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.7rem 0.85rem;
}
.prd-analysis-title {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: var(--brand-navy-800);
    font-size: 0.78rem;
    font-weight: 700;
}
.prd-analysis-title .icon,
.prd-analysis-detail .icon {
    color: var(--brand-teal-600);
}
.prd-analysis-details {
    display: grid;
    gap: 0.75rem;
    padding: 0.75rem 0.85rem;
    border-top: 1px solid #e6efed;
}
.prd-analysis-detail {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    min-width: 0;
}
.prd-analysis-detail > div {
    min-width: 0;
}
.prd-analysis-detail small {
    display: block;
    margin-bottom: 0.15rem;
    color: #667789;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}
.prd-analysis-detail strong,
.prd-analysis-detail p {
    color: var(--brand-navy-800);
    font-size: 0.82rem;
    line-height: 1.45;
}
.prd-analysis-notes p {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}
.prd-item-price {
    color: var(--brand-navy-800);
    font-size: 0.8rem;
}
.prd-item-material::before {
    content: '·';
    margin-right: 0.3rem;
    color: #a2adbb;
}
.prd-production-strip {
    display: flex;
    gap: 1.5rem;
    background: #f7f9fc;
    border-radius: 9px;
    padding: 0.65rem 0.8rem;
    margin-top: 0.75rem;
}
.prd-production-strip span {
    display: flex;
    flex-direction: column;
}
.prd-production-strip small,
.prd-commercial-value small {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #8492a6;
}
.prd-commercial-value {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 0.75rem;
}
.prd-commercial-value .field {
    max-width: 240px;
    margin: 0;
}
.prd-commercial-value > div:last-child {
    display: flex;
    flex-direction: column;
    text-align: right;
}
.prd-item-actions {
    display: flex;
    gap: 0.3rem;
}
.prd-form-actions {
    position: sticky;
    bottom: 0;
    z-index: 5;
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    margin-top: 1rem;
    padding: 0.85rem 1rem;
    border: 1px solid #e1e7ee;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 -5px 20px rgba(31, 53, 78, 0.06);
    backdrop-filter: blur(8px);
}
.prd-company-modal {
    width: min(600px, calc(100vw - 2rem));
}
@media (max-width: 1000px) {
    .prd-context-bar {
        grid-template-columns: 1fr 1fr;
    }
}
@media (max-width: 650px) {
    .prd-page {
        padding: 1rem;
    }
    .prd-context-bar {
        grid-template-columns: 1fr;
    }
    .prd-client-field {
        align-items: stretch;
        flex-direction: column;
    }
    .prd-client-field .button {
        width: 100%;
    }
    .prd-item-card {
        flex-wrap: wrap;
    }
    .prd-item-title {
        flex-direction: column;
    }
    .prd-production-strip {
        flex-direction: column;
        gap: 0.5rem;
    }
    .prd-form-actions {
        padding: 0.75rem;
    }
    .prd-form-actions .button {
        flex: 1;
    }
    .prd-version-option {
        align-items: flex-start;
        flex-direction: column;
        gap: 0.8rem;
    }
}
</style>
